import { useState, useEffect } from "react";
import { FormData, SetFormData } from "./FormData.tsx";
import {
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  TruckIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

interface listingForm {
  formData: FormData;
  setFormData: SetFormData;
  setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

function CollapsibleSection({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
        )}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

const ZONING_OPTIONS = ["", "Commercial", "Industrial", "Mixed-Use", "Retail", "Office", "Residential"];
const BUILDING_CLASS_OPTIONS = ["", "Class A", "Class B", "Class C"];
const DOCK_HEIGHT_OPTIONS = ["", "Grade Level", "Dock High", "Both"];
const TRUCK_SPACE_OPTIONS = ["", "Ample", "Moderate", "Limited"];
const TRANSIT_OPTIONS = ["", "Adjacent (< 100m)", "Walking Distance (< 500m)", "Nearby (< 1km)", "Driving Distance (> 1km)"];
const BUILD_OUT_OPTIONS = ["", "Shell", "Vanilla Box", "Turnkey", "Partially Built Out"];
const UTILITY_OPTIONS = ["Electricity", "Gas", "Water", "Sewer", "Internet", "Trash Removal", "HVAC"];

export default function WorkspaceCommercialDetails({
  formData,
  setFormData,
  setNextDisabled,
}: listingForm) {
  const handleInputChange = (
    field: keyof FormData,
    value: string | number | boolean | string[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleUtility = (utility: string) => {
    const current = formData.includedUtilities || [];
    if (current.includes(utility)) {
      handleInputChange(
        "includedUtilities",
        current.filter((u) => u !== utility)
      );
    } else {
      handleInputChange("includedUtilities", [...current, utility]);
    }
  };

  // All fields optional — never block next
  useEffect(() => {
    setNextDisabled(false);
  }, []);

  return (
    <div className="space-y-4">
      <h2 className="text-3xl font-bold mb-4">
        Commercial &amp; property details
      </h2>
      <p className="text-gray-500 text-sm mb-2">
        All fields on this page are optional. Fill in what applies to your
        workspace.
      </p>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />

      {/* Section 1: Building Details */}
      <CollapsibleSection
        title="Building Details"
        icon={BuildingOffice2Icon}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ceiling Height (m)
            </label>
            <input
              type="number"
              value={formData.ceilingHeight || ""}
              onChange={(e) =>
                handleInputChange("ceilingHeight", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.1"
              placeholder="e.g. 3.5"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Floor Load Capacity (kg/m²)
            </label>
            <input
              type="number"
              value={formData.floorLoadCapacity || ""}
              onChange={(e) =>
                handleInputChange("floorLoadCapacity", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              placeholder="e.g. 500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zoning Type
            </label>
            <select
              value={formData.zoningType}
              onChange={(e) =>
                handleInputChange("zoningType", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {ZONING_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Building Class
            </label>
            <select
              value={formData.buildingClass}
              onChange={(e) =>
                handleInputChange("buildingClass", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {BUILDING_CLASS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year Built
            </label>
            <input
              type="number"
              value={formData.yearBuilt || ""}
              onChange={(e) =>
                handleInputChange("yearBuilt", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="1800"
              max={new Date().getFullYear()}
              placeholder="e.g. 2005"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Renovation Year
            </label>
            <input
              type="number"
              value={formData.renovationYear || ""}
              onChange={(e) =>
                handleInputChange("renovationYear", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="1800"
              max={new Date().getFullYear()}
              placeholder="e.g. 2020"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 2: Financial Details */}
      <CollapsibleSection title="Financial Details" icon={CurrencyDollarIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Security Deposit ($)
            </label>
            <input
              type="number"
              value={formData.securityDeposit || ""}
              onChange={(e) =>
                handleInputChange("securityDeposit", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="e.g. 5000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Operating Expenses ($/yr)
            </label>
            <input
              type="number"
              value={formData.operatingExpenses || ""}
              onChange={(e) =>
                handleInputChange("operatingExpenses", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="Triple Net (NNN) costs"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax Assessment ($/yr)
            </label>
            <input
              type="number"
              value={formData.taxAssessment || ""}
              onChange={(e) =>
                handleInputChange("taxAssessment", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="Annual property taxes"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Common Area Maintenance ($/yr)
            </label>
            <input
              type="number"
              value={formData.commonAreaMaintenance || ""}
              onChange={(e) =>
                handleInputChange(
                  "commonAreaMaintenance",
                  Number(e.target.value)
                )
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="CAM fees"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Included Utilities
          </label>
          <div className="flex flex-wrap gap-2">
            {UTILITY_OPTIONS.map((utility) => (
              <label
                key={utility}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                  formData.includedUtilities?.includes(utility)
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.includedUtilities?.includes(utility) || false}
                  onChange={() => toggleUtility(utility)}
                  className="sr-only"
                />
                {utility}
              </label>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 3: Industrial & Loading */}
      <CollapsibleSection title="Industrial & Loading" icon={TruckIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loading Dock Count
            </label>
            <input
              type="number"
              value={formData.loadingDockCount || ""}
              onChange={(e) =>
                handleInputChange("loadingDockCount", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Number of loading docks"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dock Height
            </label>
            <select
              value={formData.dockHeight}
              onChange={(e) =>
                handleInputChange("dockHeight", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {DOCK_HEIGHT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Truck Maneuvering Space
            </label>
            <select
              value={formData.truckManeuveringSpace}
              onChange={(e) =>
                handleInputChange("truckManeuveringSpace", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {TRUCK_SPACE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.driveInDoors || false}
              onChange={(e) =>
                handleInputChange("driveInDoors", e.target.checked)
              }
              className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium">Drive-in Doors</span>
          </label>
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.overheadCranes || false}
              onChange={(e) =>
                handleInputChange("overheadCranes", e.target.checked)
              }
              className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium">Overhead Cranes</span>
          </label>
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sprinklerSystem || false}
              onChange={(e) =>
                handleInputChange("sprinklerSystem", e.target.checked)
              }
              className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium">Sprinkler System</span>
          </label>
        </div>
      </CollapsibleSection>

      {/* Section 4: Parking & Access */}
      <CollapsibleSection title="Parking & Access" icon={MapPinIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Parking Spots
            </label>
            <input
              type="number"
              value={formData.parkingSpotsTotal || ""}
              onChange={(e) =>
                handleInputChange("parkingSpotsTotal", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Total available spots"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reserved Parking Spots
            </label>
            <input
              type="number"
              value={formData.parkingSpotsReserved || ""}
              onChange={(e) =>
                handleInputChange(
                  "parkingSpotsReserved",
                  Number(e.target.value)
                )
              }
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Spots included with lease"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Parking Fee ($)
            </label>
            <input
              type="number"
              value={formData.parkingFee || ""}
              onChange={(e) =>
                handleInputChange("parkingFee", Number(e.target.value))
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="Per spot per month"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              EV Charging Stations
            </label>
            <input
              type="number"
              value={formData.electricVehicleCharging || ""}
              onChange={(e) =>
                handleInputChange(
                  "electricVehicleCharging",
                  Number(e.target.value)
                )
              }
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Number of EV stations"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Public Transport Proximity
            </label>
            <select
              value={formData.publicTransportProximity}
              onChange={(e) =>
                handleInputChange("publicTransportProximity", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {TRANSIT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 5: Tenant Improvement */}
      <CollapsibleSection
        title="Tenant Improvement"
        icon={WrenchScrewdriverIcon}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tenant Improvement Allowance ($)
            </label>
            <input
              type="number"
              value={formData.tenantImprovementAllowance || ""}
              onChange={(e) =>
                handleInputChange(
                  "tenantImprovementAllowance",
                  Number(e.target.value)
                )
              }
              className="w-full p-2 border rounded"
              min="0"
              step="0.01"
              placeholder="TI allowance amount"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Build-Out Status
            </label>
            <select
              value={formData.buildOutStatus}
              onChange={(e) =>
                handleInputChange("buildOutStatus", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              {BUILD_OUT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.landlordWorkLetter || false}
              onChange={(e) =>
                handleInputChange("landlordWorkLetter", e.target.checked)
              }
              className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium">
              Landlord will complete improvements (Work Letter)
            </span>
          </label>
        </div>
      </CollapsibleSection>
    </div>
  );
}
